import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { formatPhoneNumber } from '@leedpulse-monorepo/utils';
import { AiReceptionistCall, AiReceptionistCallTask } from '../../../../domain/models/AiReceptionist';

dayjs.locale('fr');

export type ReceptionistCallsPeriod = 'today' | 'last_7_days' | 'this_month' | 'last_month' | 'custom';
export type ReceptionistCallsContentFilter = 'with_content' | 'without_content' | 'all';

export interface ReceptionistCallsRange {
  start: number;
  end: number;
}

export const RECEPTIONIST_CALLS_PERIOD_OPTIONS: { value: ReceptionistCallsPeriod; label: string }[] = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'last_7_days', label: '7 derniers jours' },
  { value: 'this_month', label: 'Ce mois-ci' },
  { value: 'last_month', label: 'Mois dernier' },
  { value: 'custom', label: 'Période personnalisée' },
];

export const RECEPTIONIST_CALLS_CONTENT_OPTIONS: { value: ReceptionistCallsContentFilter; label: string }[] = [
  { value: 'with_content', label: 'Avec contenu' },
  { value: 'without_content', label: 'Sans contenu' },
  { value: 'all', label: 'Tous les appels' },
];

// A contact created within this window around the call is considered created by Donna
const NEW_CONTACT_WINDOW_MS = 10 * 60 * 1000;

export interface ReceptionistCallDisplay {
  id: string;
  subscriptionId: string;
  created: number;
  callerName: string;
  callerPhone: string;
  initials: string;
  title: string | null;
  preview: string;
  summaryMarkdown: string;
  motifLabel: string | null;
  urgencyReason: string;
  durationMs: number;
  isUrgent: boolean;
  hasContent: boolean;
  isNewContact: boolean;
  isOpportunity: boolean;
  isUnread: boolean;
  isOwn: boolean;
  assistantName: string;
  userName: string;
  tasks: AiReceptionistCallTask[];
  hasTranscript: boolean;
  hasRecording: boolean;
}

export interface ReceptionistTranscriptMessage {
  isAssistant: boolean;
  time: string;
  text: string;
}

export interface ReceptionistCallsDayGroup {
  key: string;
  label: string;
  calls: ReceptionistCallDisplay[];
}

// The custom period has no range of its own: the controller holds the dates applied by the user
export function getReceptionistCallsPeriodRange(period: Exclude<ReceptionistCallsPeriod, 'custom'>, now = dayjs()): ReceptionistCallsRange {
  switch (period) {
    case 'today':
      return { start: now.startOf('day').valueOf(), end: now.endOf('day').valueOf() };
    case 'last_7_days':
      return { start: now.subtract(6, 'day').startOf('day').valueOf(), end: now.endOf('day').valueOf() };
    case 'last_month': {
      const lastMonth = now.subtract(1, 'month');
      return { start: lastMonth.startOf('month').valueOf(), end: lastMonth.endOf('month').valueOf() };
    }
    default:
      return { start: now.startOf('month').valueOf(), end: now.endOf('day').valueOf() };
  }
}

// Whole days from the first to the last date picked, null while the range is incomplete or reversed
export function getReceptionistCallsCustomRange(start: Dayjs | null, end: Dayjs | null): ReceptionistCallsRange | null {
  if (!start || !end || start.isAfter(end, 'day')) return null;
  return { start: start.startOf('day').valueOf(), end: end.endOf('day').valueOf() };
}

// Same extraction as mobile getCallSummaryDisplay (mobile/src/features/@shared/utils/callSummaryDisplay.ts)
function getSummaryParts(transcription: string): { heading: string | null; body: string; bodyLines: string[] } {
  const lines = transcription.split('\n');
  const headingLine = lines.find((line) => /^#\s/.test(line));
  const heading = headingLine ? headingLine.replace(/^#\s+/, '').replace(/^⚠️\s*/, '').trim() : null;
  const bodyMarkdownLines = headingLine ? lines.filter((line) => line !== headingLine) : lines;
  const bodyLines = bodyMarkdownLines
    .filter((line) => !/^#{1,6}\s/.test(line))
    .map((line) => line.replace(/[*_`~]/g, '').replace(/^[-•]\s+/, '').trim())
    .filter((line) => line.length > 0);

  return { heading, body: bodyMarkdownLines.join('\n').trim(), bodyLines };
}

// The backend writes the receptionist SRT with `[Agent IA]` for Donna (buildSrtTranscript, backend/src/helpers/elevenlabs.helpers.ts)
export function parseReceptionistTranscript(srt: string): ReceptionistTranscriptMessage[] {
  if (!srt) return [];

  return srt
    .trim()
    .split(/\n\s*\n/)
    .map((block) => block.trim().split('\n'))
    .filter((lines) => lines.length >= 4)
    .map(([, timing, speaker, ...textLines]) => {
      const [, hours = '0', minutes = '0', seconds = '0'] = timing.match(/^(\d+):(\d+):(\d+)/) ?? [];
      const totalMinutes = Number(hours) * 60 + Number(minutes);
      return {
        isAssistant: speaker.trim() === '[Agent IA]',
        time: `${totalMinutes}:${seconds.padStart(2, '0')}`,
        text: textLines.join(' ').trim(),
      };
    })
    .filter((message) => message.text.length > 0);
}

function getInitials(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter((part) => /^\p{L}/u.test(part))
    .map((part) => part[0].toUpperCase());
  return letters.slice(0, 2).join('') || '?';
}

export function buildReceptionistCallDisplay(call: AiReceptionistCall, userName: string): ReceptionistCallDisplay {
  const contactName = call.contact ? `${call.contact.firstname} ${call.contact.lastname}`.trim() : '';
  const rawPhone = call.contact?.phone || call.customer;
  const callerPhone = rawPhone ? formatPhoneNumber(rawPhone) : '';
  const callerName = contactName || call.ai_summary?.caller_name || callerPhone || 'Numéro masqué';
  const { heading, body, bodyLines } = getSummaryParts(call.transcription);
  const urgency = call.ai_summary?.urgency;

  return {
    id: call.id,
    subscriptionId: call.subscription_id,
    created: call.created,
    callerName,
    callerPhone,
    initials: getInitials(callerName),
    title: call.ai_summary?.motif_label ?? heading,
    preview: bodyLines[0] ?? '',
    summaryMarkdown: body,
    motifLabel: call.ai_summary?.motif_label ?? null,
    urgencyReason: call.ai_summary?.urgency_reason ?? '',
    durationMs: call.duration_ms,
    isUrgent: urgency === 'immediate' || urgency === 'today',
    hasContent: call.transcription.trim().length > 0,
    isNewContact: !!call.contact?.created && Math.abs(call.contact.created - call.created) <= NEW_CONTACT_WINDOW_MS,
    isOpportunity: !!call.contact?.has_tags,
    isUnread: !call.readed_frontend,
    isOwn: call.is_own,
    assistantName: call.assistant_name || 'Donna',
    userName,
    tasks: call.tasks ?? [],
    hasTranscript: call.has_transcript,
    hasRecording: call.has_recording,
  };
}

export function groupReceptionistCallsByDay(calls: ReceptionistCallDisplay[], now = dayjs()): ReceptionistCallsDayGroup[] {
  const groups: ReceptionistCallsDayGroup[] = [];

  calls.forEach((call) => {
    const day = dayjs(call.created);
    const key = day.format('YYYY-MM-DD');
    let group = groups.find((item) => item.key === key);
    if (!group) {
      const prefix = day.isSame(now, 'day') ? "Aujourd'hui · " : day.isSame(now.subtract(1, 'day'), 'day') ? 'Hier · ' : '';
      const label = prefix ? `${prefix}${day.format('D MMMM')}` : day.format('dddd D MMMM');
      group = { key, label: label.charAt(0).toUpperCase() + label.slice(1), calls: [] };
      groups.push(group);
    }
    group.calls.push(call);
  });

  return groups;
}

export interface ReceptionistDefaultCall {
  filtersKey: string;
  id: string | null;
}

// Without a click the most recent call is shown, pinned: a later page can insert a more recent one of another
// receptionist, which must not swap the detail under the user. Pinned again when the filters change or the call leaves the list
export function pinReceptionistDefaultCall(
  defaultCall: ReceptionistDefaultCall,
  calls: ReceptionistCallDisplay[],
  filtersKey: string,
): ReceptionistDefaultCall {
  const firstCallId = calls[0]?.id ?? null;
  const isDefaultCallListed = calls.some((call) => call.id === defaultCall.id);
  if (defaultCall.filtersKey !== filtersKey || (!isDefaultCallListed && defaultCall.id !== firstCallId)) {
    return { filtersKey, id: firstCallId };
  }
  return defaultCall;
}

export function formatReceptionistCallDuration(durationMs: number): string {
  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds} s`;
  return `${minutes} min ${seconds} s`;
}
